import sort from 'src/utils/sort'

import discrepancy from 'src/delivery/store/utils/discrepancy'
import options from 'src/delivery/store/utils/options'
import osi from 'src/delivery/store/utils/osi'

import deliverySummaries from 'src/delivery/store/utils/summaries'
import internalSummaries from 'src/internal/store/utils/summaries'
import programmaticSummaries from 'src/programmatic/store/utils/summaries'

import dashboardColumns from 'src/dashboard/store/utils/columns'
import deliveryColumns from 'src/delivery/store/utils/columns'
import devColumns from 'src/develop/store/utils/columns'
import homeColumns from 'src/home/store/utils/columns'
import internalColumns from 'src/internal/store/utils/columns'
import programmaticColumns from 'src/programmatic/store/utils/columns'

import PATHS from 'src/router/paths'
import search from './utils/search'

const searchEndpoints = search.endpoints

const {
  MAX_RECENT_SEARCHES,
  SEARCH_STORAGE_KEY,
  SEARCH_ID_KEY,
  SEARCH_OPTIONS,
  SEARCH_TEXT_ALT,
  SEARCH_TEXT_KEY,
} = search.config

const { direction: SortDirection } = sort

const state = () => ({
  SEARCH_ID_KEY,
  MAX_RECENT_SEARCHES,
  MAX_SEARCH_RESULTS: 3,
  SEARCH_STORAGE_KEY,
  SEARCH_OPTIONS,
  SEARCH_TEXT_KEY,
  SEARCH_TEXT_ALT,

  adjuster: false,
  navigation: [],
  searchData: {},
  searchOptions: {},
  searchResults: {
    endpoints: searchEndpoints,
    total: {
      items: 0,
      recent: 0,
      results: 0,
    },
  },
  searchTerm: '',
  recentSearches: [],

  isLoadingMainContent: false,

  isLoadingSearch: false,
  didLoadSearchFail: false,

  selected: null,

  baseReportName: null,
  routeName: null,
  reportPath: null,
  reportPathDefault: 'all-data',
  query: null,
  metricPath: null,

  reports: {},
  reportManager: {},
  temporaryReportOptions: [],
  unwatchReportFnsByDocId: [],

  associationId: null,
  subId: null,

  associationIds: {},
  subIds: {},
  selectedIds: {},

  viewIds: {},

  dragging: false,

  dataRefreshReport: null,
  showDataFreshnessLegend: false,

  didLoadDataRefreshReportFail: false,
  isLoadingDataRefreshReport: false,

  dataRefreshedTime: null,
  isDataFresh: null,

  didLoadDataRefreshedTimeFail: false,
  isLoadingDataRefreshedTime: false,

  // @TODO Remove endpointMap, as we no longer need to map endpoint names to plural instances.
  endpointMap: {
    [PATHS.ADMANAGER.BASE]: PATHS.ADMANAGER.BASE,
    [PATHS.ADMANAGER.LINEITEMS]: PATHS.ADMANAGER.LINEITEMS,
    [PATHS.ADMANAGER.CREATIVES]: PATHS.ADMANAGER.CREATIVES,
    [PATHS.ADMANAGER.NETWORKS]: PATHS.ADMANAGER.NETWORKS,
    [PATHS.ADMANAGER.ORDERS]: PATHS.ADMANAGER.ORDERS,
    [PATHS.DELIVERY.DISCREPANCY]: PATHS.DELIVERY.DISCREPANCY,
    [PATHS.DELIVERY.PACING]: PATHS.DELIVERY.PACING,
    [PATHS.INTERNAL.ORGANIZATION]: PATHS.INTERNAL.ORGANIZATION,
    [PATHS.INTERNAL.TIME_ROLLUP]: PATHS.INTERNAL.TIME_ROLLUP,
    [PATHS.INTERNAL.PERSON]: PATHS.INTERNAL.PERSON,
    [PATHS.INTERNAL.PROJECT]: PATHS.INTERNAL.PROJECT,
    [PATHS.INTERNAL.TIME_SHEET]: PATHS.INTERNAL.TIME_SHEET,
    [PATHS.PROGRAMMATIC.ADX_HEALTH]: PATHS.PROGRAMMATIC.ADX_HEALTH,
    [PATHS.PROGRAMMATIC.NETWORK_HEALTH]: PATHS.PROGRAMMATIC.NETWORK_HEALTH,
    [PATHS.PROGRAMMATIC.ADX_AND_OPEN_BIDDING]: PATHS.PROGRAMMATIC.ADX_AND_OPEN_BIDDING,
  },
  accessFields: {
    [PATHS.DASHBOARD.BASE]: 'billingCode',
    [PATHS.ADMANAGER.CREATIVES]: 'networkCode',
    [PATHS.ADMANAGER.LINEITEMS]: 'networkCode',
    [PATHS.ADMANAGER.NETWORKS]: 'networkCode',
    [PATHS.ADMANAGER.ORDERS]: 'networkCode',
    [PATHS.DELIVERY.PACING]: 'hasPacing',
    [PATHS.DELIVERY.DISCREPANCY]: 'hasDiscrepancy',
    [PATHS.DEVELOP.BASE]: 'access.permissions.dev',
    [PATHS.INTERNAL.ORGANIZATION]: 'billingCode',
    [PATHS.INTERNAL.TIME_ROLLUP]: 'billingCode',
    [PATHS.INTERNAL.PERSON]: 'billingCode',
    [PATHS.INTERNAL.PROJECT]: 'hasProjects',
    [PATHS.INTERNAL.TIME_SHEET]: 'isTimesheetEnabled',
    [PATHS.PROGRAMMATIC.ADX_HEALTH]: 'hasHealthAdx',
    [PATHS.PROGRAMMATIC.NETWORK_HEALTH]: 'hasHealthNetwork',
    [PATHS.PROGRAMMATIC.ADX_AND_OPEN_BIDDING]: 'hasYieldPartner',
  },
  keyFields: {
    [PATHS.ADMANAGER.BASE]: 'networkCode',
    [PATHS.ADMANAGER.LINEITEMS]: 'id',
    [PATHS.ADMANAGER.CREATIVES]: 'id',
    [PATHS.ADMANAGER.NETWORKS]: 'networkCode',
    [PATHS.ADMANAGER.ORDERS]: 'id',
    [PATHS.PROGRAMMATIC.ADX_HEALTH]: 'adxHealthId',
    [PATHS.PROGRAMMATIC.NETWORK_HEALTH]: 'networkHealthId',
    discrepancy: 'lineItemDiscrepancyId',
    [PATHS.PROGRAMMATIC.ADX_AND_OPEN_BIDDING]: 'programmaticDeliveryId',
    organization: 'billingCode',
    [PATHS.INTERNAL.TIME_ROLLUP]: 'timeEntryId',
    pacing: 'lineItemId',
    person: 'email',
    project: 'projectId',
    [PATHS.INTERNAL.TIME_SHEET]: 'timeEntryId',
  },
  sizeFields: {
    [PATHS.ADMANAGER.BASE]: 'networkName',
    [PATHS.ADMANAGER.LINEITEMS]: 'name',
    [PATHS.ADMANAGER.CREATIVES]: 'name',
    [PATHS.ADMANAGER.NETWORKS]: 'networkName',
    [PATHS.ADMANAGER.ORDERS]: 'name',
    [PATHS.PROGRAMMATIC.ADX_HEALTH]: 'topLevelAdUnitName',
    [PATHS.PROGRAMMATIC.NETWORK_HEALTH]: 'topLevelAdUnitName',
    discrepancy: 'orderName',
    [PATHS.PROGRAMMATIC.ADX_AND_OPEN_BIDDING]: 'yieldPartnerName',
    organization: 'orgName',
    [PATHS.INTERNAL.TIME_ROLLUP]: 'workNote',
    pacing: 'orderName',
    person: 'email',
    project: 'projectName',
    [PATHS.INTERNAL.TIME_SHEET]: 'workNote',
  },
  columns: {},

  isCurrentlyMerging: false,
  mergeByProperties: {},
  merging: {
    [PATHS.ADMANAGER.BASE]: null,
    [PATHS.ADMANAGER.LINEITEMS]: null,
    [PATHS.ADMANAGER.CREATIVES]: null,
    [PATHS.ADMANAGER.NETWORKS]: null,
    [PATHS.ADMANAGER.ORDERS]: null,
    [PATHS.PROGRAMMATIC.ADX_HEALTH]: null,
    [PATHS.PROGRAMMATIC.NETWORK_HEALTH]: null,
    [PATHS.PROGRAMMATIC.ADX_AND_OPEN_BIDDING]: null,
  },

  isCurrentlyGrouping: false,
  groupByProperties: {},
  grouping: {
    [PATHS.ADMANAGER.BASE]: null,
    [PATHS.ADMANAGER.LINEITEMS]: null,
    [PATHS.ADMANAGER.CREATIVES]: null,
    [PATHS.ADMANAGER.NETWORKS]: null,
    [PATHS.ADMANAGER.ORDERS]: null,
    [PATHS.PROGRAMMATIC.ADX_HEALTH]: null,
    [PATHS.PROGRAMMATIC.NETWORK_HEALTH]: null,
    discrepancy: null,
    [PATHS.PROGRAMMATIC.ADX_AND_OPEN_BIDDING]: ['demandChannelName'],
    organization: null,
    [PATHS.INTERNAL.TIME_ROLLUP]: null,
    pacing: null,
    person: null,
    project: null,
    [PATHS.INTERNAL.TIME_SHEET]: ['timeEntryDate'],
  },

  columnDefaults: Object.freeze({
    ...dashboardColumns,
    ...deliveryColumns,
    ...devColumns,
    ...homeColumns,
    ...internalColumns,
    ...programmaticColumns,
  }),

  summaries: {
    [PATHS.PROGRAMMATIC.ADX_HEALTH]: {
      defaults: JSON.parse(JSON.stringify(programmaticSummaries[PATHS.PROGRAMMATIC.ADX_HEALTH])),
    },
    [PATHS.PROGRAMMATIC.NETWORK_HEALTH]: {
      defaults: JSON.parse(JSON.stringify(programmaticSummaries[PATHS.PROGRAMMATIC.NETWORK_HEALTH])),
    },
    [PATHS.PROGRAMMATIC.ADX_AND_OPEN_BIDDING]: {
      defaults: JSON.parse(JSON.stringify(programmaticSummaries[PATHS.PROGRAMMATIC.ADX_AND_OPEN_BIDDING])),
    },
    [PATHS.INTERNAL.ORGANIZATION]: {
      defaults: Object.freeze(internalSummaries[PATHS.INTERNAL.ORGANIZATION]),
    },
    [PATHS.INTERNAL.TIME_ROLLUP]: {
      defaults: Object.freeze(internalSummaries[PATHS.INTERNAL.TIME_ROLLUP]),
    },
    [PATHS.DELIVERY.PACING]: {
      defaults: JSON.parse(JSON.stringify(deliverySummaries.pacing)),
    },
    [PATHS.INTERNAL.PERSON]: {
      defaults: Object.freeze(internalSummaries[PATHS.INTERNAL.PERSON]),
    },
    [PATHS.INTERNAL.PROJECT]: {
      defaults: Object.freeze(internalSummaries[PATHS.INTERNAL.PROJECT]),
    },
    [PATHS.INTERNAL.TIME_SHEET]: {
      defaults: Object.freeze(internalSummaries[PATHS.INTERNAL.TIME_SHEET]),
    },
  },
  summaryDefaults: {
    [PATHS.ADMANAGER.BASE]: {},
    [PATHS.ADMANAGER.LINEITEMS]: {},
    [PATHS.ADMANAGER.CREATIVES]: {},
    [PATHS.ADMANAGER.NETWORKS]: {},
    [PATHS.ADMANAGER.ORDERS]: {},
    [PATHS.PROGRAMMATIC.ADX_HEALTH]: Object.freeze(programmaticSummaries[PATHS.PROGRAMMATIC.ADX_HEALTH]),
    [PATHS.PROGRAMMATIC.NETWORK_HEALTH]: Object.freeze(programmaticSummaries[PATHS.PROGRAMMATIC.NETWORK_HEALTH]),
    [PATHS.PROGRAMMATIC.ADX_AND_OPEN_BIDDING]: Object.freeze(programmaticSummaries[PATHS.PROGRAMMATIC.ADX_AND_OPEN_BIDDING]),
    [PATHS.INTERNAL.ORGANIZATION]: Object.freeze(internalSummaries[PATHS.INTERNAL.ORGANIZATION]),
    [PATHS.INTERNAL.TIME_ROLLUP]: Object.freeze(internalSummaries[PATHS.INTERNAL.TIME_ROLLUP]),
    [PATHS.DELIVERY.PACING]: Object.freeze(deliverySummaries.pacing),
    [PATHS.INTERNAL.PERSON]: Object.freeze(internalSummaries[PATHS.INTERNAL.PERSON]),
    [PATHS.INTERNAL.PROJECT]: Object.freeze(internalSummaries[PATHS.INTERNAL.PROJECT]),
    [PATHS.INTERNAL.TIME_SHEET]: Object.freeze(internalSummaries[PATHS.INTERNAL.TIME_SHEET]),
  },
  selectedSummary: {
    [PATHS.ADMANAGER.BASE]: {},
    [PATHS.ADMANAGER.LINEITEMS]: {},
    [PATHS.ADMANAGER.CREATIVES]: {},
    [PATHS.ADMANAGER.NETWORKS]: {},
    [PATHS.ADMANAGER.ORDERS]: {},
    [PATHS.PROGRAMMATIC.ADX_HEALTH]: {},
    [PATHS.PROGRAMMATIC.NETWORK_HEALTH]: {},
    [PATHS.PROGRAMMATIC.ADX_AND_OPEN_BIDDING]: {},
    [PATHS.INTERNAL.ORGANIZATION]: {},
    [PATHS.INTERNAL.TIME_ROLLUP]: {},
    [PATHS.DELIVERY.PACING]: {},
    [PATHS.INTERNAL.PERSON]: {},
    [PATHS.INTERNAL.PROJECT]: {},
    [PATHS.INTERNAL.TIME_SHEET]: {},
  },

  infoModal: {},
  isInfoModalVisible: false,

  isCurrentlyAskingForDataFromTheApi: false,

  isSorting: {},
  sorted: {},
  sorting: {
    [PATHS.ADMANAGER.BASE]: [],
    [PATHS.ADMANAGER.LINEITEMS]: [],
    [PATHS.ADMANAGER.CREATIVES]: [],
    [PATHS.ADMANAGER.NETWORKS]: [],
    [PATHS.ADMANAGER.ORDERS]: [],
    [PATHS.PROGRAMMATIC.ADX_HEALTH]: [
      { direction: SortDirection.DSC, key: 'date' },
    ],
    [PATHS.PROGRAMMATIC.NETWORK_HEALTH]: [
      { direction: SortDirection.DSC, key: 'date' },
    ],
    discrepancy: [
      { direction: SortDirection.ASC, key: 'advertiserName' },
      { direction: SortDirection.ASC, key: 'orderName' },
    ],
    [PATHS.PROGRAMMATIC.ADX_AND_OPEN_BIDDING]: [
      { direction: SortDirection.DSC, key: 'date' },
    ],
    [PATHS.INTERNAL.ORGANIZATION]: [
      { direction: SortDirection.ASC, key: 'billingCode' },
    ],
    [PATHS.INTERNAL.TIME_ROLLUP]: [
      { direction: SortDirection.ASC, key: 'billingCode' },
    ],
    [PATHS.INTERNAL.PACING]: [
      { direction: SortDirection.ASC, key: 'advertiserName' },
      { direction: SortDirection.ASC, key: 'orderName' },
    ],
    [PATHS.DELIVERY.PERSON]: [
      { direction: SortDirection.ASC, key: 'personNameLast' },
      { direction: SortDirection.ASC, key: 'personNameFirst' },
    ],
    [PATHS.INTERNAL.PROJECT]: [
      { direction: SortDirection.ASC, key: 'projectCode' },
    ],
    [PATHS.INTERNAL.TIME_SHEET]: [
      { direction: SortDirection.ASC, key: 'timeEntryId' },
    ],
  },
  sortingByKeys: null,
  sortingByDirections: null,

  itemsPerPage: 100,
  itemsPerPageOptions: [
    { text: 'All', value: -1 },
    { text: '100 / Page', value: 100 },
    { text: '500 / Page', value: 500 },
    { text: '1000 / Page', value: 1000 },
  ],
  page: 1,
  pageCount: 0,

  requiredColumns: {
    pacing: ['lineItemName'],
  },
  sortClassIndicatorMap: {
    end: 'order-1',
    center: 'order-2',
    start: 'order-2',
  },
  sortClassTextMap: {
    end: 'order-2 ml-1',
    center: 'order-1 mr-1',
    start: 'order-1 mr-1',
  },
  sortIcons: {
    ASC: 'keyboard_arrow_down',
    false: 'keyboard_arrow_down',

    DSC: 'keyboard_arrow_up',
    true: 'keyboard_arrow_up',
  },
  tableSortMap: {
    ASC: false,
    DSC: true,
  },

  displayFilter: false,
  applyingFilter: false,
  resettingFilter: false,

  displayAdvancedTools: false,
  activePreFilters: {},
  ignorePreFilters: {},

  numberOfAppliedFilters: {},

  filterData: {},
  filterDefaults: {},

  booleanOptions: [
    {
      operator: 'is',
      text: 'False',
      value: false,
    },
    {
      operator: 'is',
      text: 'True',
      value: true,
    },
  ],
  booleanOptionsAlt: [
    {
      operator: 'is',
      text: 'No',
      value: 'false',
    },
    {
      operator: 'is',
      text: 'Yes',
      value: 'true',
    },
  ],

  hasClickedToggler: false,
  isTogglingTables: false,
  showRows: [],

  snackbar: false,
  snack: { message: null, status: null },

  modalbar: false,
  modal: { actions: [], status: null, text: [], title: '' },

  admanagerRoutes: ['admanager', 'creatives', 'lineitems', 'networks', 'orders'],
  dashboardRoutes: ['dashboard', 'home'],
  devRoutes: ['develop'],
  deliveryRoutes: ['delivery', 'discrepancy', 'pacing'],
  docsRoutes: ['docs', 'backlog', 'data-refresh', 'deprecation', 'privacy-policy', 'releases', 'whitelist'],
  programmaticRoutes: ['programmatic', 'adx-health', 'network-health', 'adx-and-open-bidding'],
  customAnalyticRoutes: [
    'custom-analytics',
    'campaign-tracking',
    'campaign-tracking-1',
    'campaign-tracking-2',
    'monthly-progress',
    'progress-report',
    'reportlist',
    'revenue',
    'revenue-at-risk',
  ],
  timeRoutes: [
    PATHS.INTERNAL.TIME_ROLLUP,
    PATHS.INTERNAL.TIME_SHEET,
  ],
  internalRoutes: [
    PATHS.INTERNAL.BASE,
    PATHS.INTERNAL.ORGANIZATION,
    PATHS.INTERNAL.TIME_ROLLUP,
    PATHS.INTERNAL.PERSON,
    PATHS.INTERNAL.PROJECT,
    PATHS.INTERNAL.TIME_SHEET,
  ],

  indicatorOptions: Object.keys(options).map((key) => options[key]),
  osiOptions: Object.keys(osi.options).map((key) => osi.options[key]),
  discrepancyOptions: Object.keys(discrepancy.options).map((key) => discrepancy.options[key]),
  groupingOptions: {
    [PATHS.ADMANAGER.BASE]: [],
    [PATHS.ADMANAGER.CREATIVES]: [],
    [PATHS.ADMANAGER.LINEITEMS]: [],
    [PATHS.ADMANAGER.NETWORKS]: [],
    [PATHS.ADMANAGER.ORDERS]: [],
    [PATHS.DELIVERY.DISCREPANCY]: [
      { text: 'Advertiser', value: 'advertiserName' },
      { text: 'End Date', value: 'endDate' },
      { text: 'Line Item', value: 'lineItemName' },
      { text: 'Order', value: 'orderName' },
      { text: 'Start Date', value: 'startDate' },
    ],
    [PATHS.DELIVERY.PACING]: [
      { text: 'Advertiser', value: 'advertiserName' },
      { text: 'End Date', value: 'endDate' },
      { text: 'Line Item', value: 'lineItemName' },
      { text: 'Order', value: 'orderName' },
      { text: 'Secondary Traffickers', value: 'secondaryTraffickerNames' },
      { text: 'Sales Person', value: 'salespersonName' },
      { text: 'Sizes', value: 'creativeSizes' },
      { text: 'Start Date', value: 'startDate' },
      { text: 'Status', value: 'status' },
      { text: 'Trafficker', value: 'traffickerName' },
      { text: 'Type', value: 'type' },
      { text: 'Type & Priority', value: 'gamPriority' },
    ],
    [PATHS.INTERNAL.TIME_SHEET]: [
      { text: 'Billing Code', value: 'billingCode' },
      { text: 'Client Name', value: 'clientName' },
      { text: 'Person Name', value: 'personName' },
      { text: 'Time Entry Date', value: 'timeEntryDate' },
      { text: 'Billable Action Duration', value: 'billableActionDuration' },
      { text: 'Time Classification Name', value: 'timeClassificationName' },
      { text: 'Project Name', value: 'projectName' },
      { text: 'Week', value: 'week' },
      { text: 'Work Note', value: 'workNote' },
    ],
    [PATHS.INTERNAL.TIME_ROLLUP]: [
      { text: 'Billing Code', value: 'billingCode' },
      { text: 'Client Name', value: 'clientName' },
      { text: 'Person Name', value: 'personName' },
      { text: 'Time Entry Date', value: 'timeEntryDate' },
      { text: 'Billable Action Duration', value: 'billableActionDuration' },
      { text: 'Time Classification Name', value: 'timeClassificationName' },
      { text: 'Project Name', value: 'projectName' },
      { text: 'Work Note', value: 'workNote' },
    ],
    [PATHS.INTERNAL.ORGANIZATION]: [
      { text: 'Billing Code', value: 'billingCode' },
      { text: 'Org Name', value: 'orgName' },
      { text: 'Network Code', value: 'networkCode' },
      { text: 'Is Active', value: 'isActive' },
      { text: 'Is Deleted', value: 'isDeleted' },
      { text: 'Is Internal Use Only', value: 'isInternalUseOnly' },
      { text: 'Is Publisher', value: 'isPublisher' },
      { text: 'Has Timesheet Report', value: 'hasTimesheetReport' },
      { text: 'Has Discrepancy Report', value: 'hasDiscrepancyReport' },
      { text: 'Has Pacing Report', value: 'hasPacingReport' },
      { text: 'Has Health Adx Report', value: 'hasHealthAdxReport' },
      { text: 'Has Health Network Report', value: 'hasHealthNetworkReport' },
      { text: 'Has Yield Partner Report', value: 'hasYieldPartnerReport' },
    ],
    [PATHS.INTERNAL.PERSON]: [
      { text: 'Person ID', value: 'personId' },
      { text: 'Person Name', value: 'personName' },
      { text: 'Email', value: 'email' },
      { text: 'Active', value: 'isPersonUserActive' },
      { text: 'Created Date', value: 'created_dt' },
      { text: 'Updated Date', value: 'updated_dt' },
      { text: 'Is Config Valid', value: 'isConfigValid' },
      { text: 'Has Access Errors', value: 'hasAccessErrors' },
      { text: 'Can Access Apps', value: 'canAccessApps' },
      { text: 'Is Person', value: 'isPerson' },
      { text: 'Is Person Authorized', value: 'isPersonAuthorized' },
      { text: 'Is Person Deleted', value: 'isDeleted' },
      { text: 'Is Person Active', value: 'isActive' },
      { text: 'Person Role', value: 'personRole' },
      { text: 'Is User', value: 'isUser' },
      { text: 'Is User Authorized', value: 'isUserAuthorized' },
      { text: 'Is User Active', value: 'isUserActive' },
      { text: 'Access Level', value: 'access.level' },
      { text: 'Access Role', value: 'access.role' },
      { text: 'Access Expires', value: 'access.expires' },
      { text: 'Billing Codes', value: 'billingCodes' },
      { text: 'Network Codes', value: 'networkCodes' },
      { text: 'Org IDs', value: 'orgIds' },
      { text: 'Org Names', value: 'orgNames' },
      { text: 'Team Codes', value: 'teamCodes' },
      { text: 'Team IDs', value: 'teamIds' },
      { text: 'Team Names', value: 'teamNames' },
      { text: 'Manager Person ID', value: 'managerPersonId' },
      { text: 'Manager Person Name', value: 'managerPersonName' },
      { text: 'Custom Analytics Active', value: 'customAnalyticsActive' },
      { text: 'Has Custom Analytics', value: 'hasCustomAnalytics' },
      { text: 'Has Filters', value: 'hasFilters' },
    ],
    [PATHS.INTERNAL.PROJECT]: [
      { text: 'Billing Code', value: 'billingCode' },
      { text: 'Is Active', value: 'isActive' },
      { text: 'Project Code', value: 'projectCode' },
      { text: 'Project Name', value: 'projectName' },
    ],
    [PATHS.PROGRAMMATIC.ADX_HEALTH]: [
      { text: 'Date', value: 'date' },
      { text: 'Device Category', value: 'deviceCategory' },
      { text: 'Top-Level Ad Unit', value: 'topLevelAdUnitName' },
      { text: 'Size', value: 'inventorySize' },
    ],
    [PATHS.PROGRAMMATIC.NETWORK_HEALTH]: [
      { text: 'Date', value: 'date' },
      { text: 'Device Category', value: 'deviceCategoryName' },
      { text: 'Top-Level Ad Unit', value: 'topLevelAdUnitName' },
      { text: 'Size', value: 'requestedAdSizes' },
    ],
    [PATHS.PROGRAMMATIC.ADX_AND_OPEN_BIDDING]: [
      { text: 'Date', value: 'date' },
      { text: 'Demand Channel', value: 'demandChannelName' },
      { text: 'Week', value: 'week' },
      { text: 'Yield Partner', value: 'yieldPartner' },
    ],
  },

  mergeMap: {
    [PATHS.ADMANAGER.BASE]: [],
    [PATHS.ADMANAGER.CREATIVES]: [],
    [PATHS.ADMANAGER.LINEITEMS]: [],
    [PATHS.ADMANAGER.NETWORKS]: [],
    [PATHS.ADMANAGER.ORDERS]: [],
    [PATHS.DELIVERY.DISCREPANCY]: [
      'advertiserName',
      'endDate',
      'lineItemName',
      'orderName',
      'startDate',
    ],
    [PATHS.DELIVERY.PACING]: [
      'advertiserName',
      'endDate',
      'lineItemName',
      'orderName',
      'secondaryTraffickerNames',
      'salespersonName',
      'creativeSizes',
      'startDate',
      'status',
      'traffickerName',
      'type',
      'gamPriority',
    ],
    [PATHS.INTERNAL.TIME_SHEET]: [
      'billingCode',
      'clientName',
      'personName',
      'timeEntryDate',
      'billableActionDuration',
      'timeClassificationName',
      'projectName',
      'workNote',
    ],
    [PATHS.INTERNAL.TIME_ROLLUP]: [
      'billingCode',
      'clientName',
      'personName',
      'timeEntryDate',
      'billableActionDuration',
      'timeClassificationName',
      'projectName',
      'workNote',
    ],
    [PATHS.INTERNAL.ORGANIZATION]: [
      'billingCode',
      'orgName',
      'networkCode',
      'isActive',
      'isDeleted',
      'isInternalUseOnly',
      'hasTimesheetReport',
      'hasDiscrepancyReport',
      'hasPacingReport',
      'hasHealthAdxReport',
      'hasHealthNetworkReport',
      'hasYieldPartnerReport',
    ],
    [PATHS.INTERNAL.PERSON]: [
      'personId',
      'personName',
      'email',
      'isPersonUserActive',
      'created_dt',
      'updated_dt',
      'isConfigValid',
      'hasAccessErrors',
      'canAccessApps',
      'isPerson',
      'isPersonAuthorized',
      'isDeleted',
      'isActive',
      'personRole',
      'isUser',
      'isUserAuthorized',
      'isUserActive',
      'access.level',
      'access.role',
      'access.expires',
      'billingCodes',
      'networkCodes',
      'orgIds',
      'orgNames',
      'teamCodes',
      'teamIds',
      'teamNames',
      'managerPersonId',
      'managerPersonName',
      'customAnalyticsActive',
      'hasCustomAnalytics',
      'hasFilters',
    ],
    [PATHS.INTERNAL.PROJECT]: [
      'projectCode',
      'projectName',
      'isActive',
    ],
    [PATHS.PROGRAMMATIC.ADX_HEALTH]: [
      'date',
      'deviceCategory',
      'topLevelAdUnitName',
      'inventorySize',
    ],
    [PATHS.PROGRAMMATIC.NETWORK_HEALTH]: [
      'date',
      'deviceCategoryName',
      'topLevelAdUnitName',
      'requestedAdSizes',
    ],
    [PATHS.PROGRAMMATIC.ADX_AND_OPEN_BIDDING]: [
      'date',
      'demandChannelName',
      'week',
      'yieldPartner',
    ],
  },
  mergingOptions: {
    [PATHS.ADMANAGER.BASE]: [],
    [PATHS.ADMANAGER.CREATIVES]: [],
    [PATHS.ADMANAGER.LINEITEMS]: [],
    [PATHS.ADMANAGER.NETWORKS]: [],
    [PATHS.ADMANAGER.ORDERS]: [],
    [PATHS.DELIVERY.DISCREPANCY]: [
      { text: 'Advertiser', value: 'advertiserName' },
      { text: 'End Date', value: 'endDate' },
      { text: 'Line Item', value: 'lineItemName' },
      { text: 'Order', value: 'orderName' },
      { text: 'Start Date', value: 'startDate' },
    ],
    [PATHS.DELIVERY.PACING]: [
      { text: 'Advertiser', value: 'advertiserName' },
      { text: 'End Date', value: 'endDate' },
      { text: 'Line Item', value: 'lineItemName' },
      { text: 'Order', value: 'orderName' },
      { text: 'Secondary Traffickers', value: 'secondaryTraffickerNames' },
      { text: 'Sales Person', value: 'salespersonName' },
      { text: 'Sizes', value: 'creativeSizes' },
      { text: 'Start Date', value: 'startDate' },
      { text: 'Status', value: 'status' },
      { text: 'Trafficker', value: 'traffickerName' },
      { text: 'Type', value: 'type' },
      { text: 'Type & Priority', value: 'gamPriority' },
    ],
    [PATHS.INTERNAL.TIME_SHEET]: [
      { text: 'Billing Code', value: 'billingCode' },
      { text: 'Client Name', value: 'clientName' },
      { text: 'Person Name', value: 'personName' },
      { text: 'Time Entry Date', value: 'timeEntryDate' },
      { text: 'Billable Action Duration', value: 'billableActionDuration' },
      { text: 'Time Classification Name', value: 'timeClassificationName' },
      { text: 'Project Name', value: 'projectName' },
      { text: 'Work Note', value: 'workNote' },
    ],
    [PATHS.INTERNAL.TIME_ROLLUP]: [
      { text: 'Billing Code', value: 'billingCode' },
      { text: 'Client Name', value: 'clientName' },
      { text: 'Person Name', value: 'personName' },
      { text: 'Time Entry Date', value: 'timeEntryDate' },
      { text: 'Billable Action Duration', value: 'billableActionDuration' },
      { text: 'Time Classification Name', value: 'timeClassificationName' },
      { text: 'Project Name', value: 'projectName' },
      { text: 'Work Note', value: 'workNote' },
    ],
    [PATHS.INTERNAL.ORGANIZATION]: [
      { text: 'Billing Code', value: 'billingCode' },
      { text: 'Org Name', value: 'orgName' },
      { text: 'Network Code', value: 'networkCode' },
      { text: 'Is Active', value: 'isActive' },
      { text: 'Is Deleted', value: 'isDeleted' },
      { text: 'Is Internal Use Only', value: 'isInternalUseOnly' },
      { text: 'Has Timesheet Report', value: 'hasTimesheetReport' },
      { text: 'Has Discrepancy Report', value: 'hasDiscrepancyReport' },
      { text: 'Has Pacing Report', value: 'hasPacingReport' },
      { text: 'Has Health Adx Report', value: 'hasHealthAdxReport' },
      { text: 'Has Health Network Report', value: 'hasHealthNetworkReport' },
      { text: 'Has Yield Partner Report', value: 'hasYieldPartnerReport' },
    ],
    [PATHS.INTERNAL.PERSON]: [
      { text: 'Person ID', value: 'personId' },
      { text: 'Person Name', value: 'personName' },
      { text: 'Email', value: 'email' },
      { text: 'Active', value: 'isPersonUserActive' },
      { text: 'Created Date', value: 'created_dt' },
      { text: 'Updated Date', value: 'updated_dt' },
      { text: 'Is Config Valid', value: 'isConfigValid' },
      { text: 'Has Access Errors', value: 'hasAccessErrors' },
      { text: 'Can Access Apps', value: 'canAccessApps' },
      { text: 'Is Person', value: 'isPerson' },
      { text: 'Is Person Authorized', value: 'isPersonAuthorized' },
      { text: 'Is Person Deleted', value: 'isDeleted' },
      { text: 'Is Person Active', value: 'isActive' },
      { text: 'Person Role', value: 'personRole' },
      { text: 'Is User', value: 'isUser' },
      { text: 'Is User Authorized', value: 'isUserAuthorized' },
      { text: 'Is User Active', value: 'isUserActive' },
      { text: 'Access Level', value: 'access.level' },
      { text: 'Access Role', value: 'access.role' },
      { text: 'Access Expires', value: 'access.expires' },
      { text: 'Billing Codes', value: 'billingCodes' },
      { text: 'Network Codes', value: 'networkCodes' },
      { text: 'Org IDs', value: 'orgIds' },
      { text: 'Org Names', value: 'orgNames' },
      { text: 'Team Codes', value: 'teamCodes' },
      { text: 'Team IDs', value: 'teamIds' },
      { text: 'Team Names', value: 'teamNames' },
      { text: 'Manager Person ID', value: 'managerPersonId' },
      { text: 'Manager Person Name', value: 'managerPersonName' },
      { text: 'Custom Analytics Active', value: 'customAnalyticsActive' },
      { text: 'Has Custom Analytics', value: 'hasCustomAnalytics' },
      { text: 'Has Filters', value: 'hasFilters' },
    ],
    [PATHS.INTERNAL.PROJECT]: [
      { text: 'Billing Code', value: 'billingCode' },
      { text: 'Is Active', value: 'isActive' },
      { text: 'Project Code', value: 'projectCode' },
      { text: 'Project Name', value: 'projectName' },
    ],
    [PATHS.PROGRAMMATIC.ADX_HEALTH]: [
      { text: 'Date', value: 'date' },
      { text: 'Device Category', value: 'deviceCategory' },
      { text: 'Top-Level Ad Unit', value: 'topLevelAdUnitName' },
      { text: 'Size', value: 'inventorySize' },
    ],
    [PATHS.PROGRAMMATIC.NETWORK_HEALTH]: [
      { text: 'Date', value: 'date' },
      { text: 'Device Category', value: 'deviceCategoryName' },
      { text: 'Top-Level Ad Unit', value: 'topLevelAdUnitName' },
      { text: 'Size', value: 'requestedAdSizes' },
    ],
    [PATHS.PROGRAMMATIC.ADX_AND_OPEN_BIDDING]: [
      { text: 'Date', value: 'date' },
      { text: 'Demand Channel', value: 'demandChannelName' },
      { text: 'Week', value: 'week' },
      { text: 'Yield Partner', value: 'yieldPartner' },
    ],
  },
})

export default state
